import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { CourseFormFields } from '@/components/ped-content/CourseForm';
import { ORDENABLE_PAGE_SIZE, REQUEST_STALE_TIME_IN_MS } from '@/constants';
import { useTreeCourses } from '@/data/context/TreeCoursesContext';
import useInfiniteService from '@/data/hook/useInfiniteService';
import {
  createCoursePath,
  deleteCoursePath,
} from '@/data/services/coursePathServices';
import { coursePathsQueryKeys } from '@/data/services/querykeys';
import alert from '@/utils/shared/alert';
import { mountKey } from '@/utils/courses';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { useListCache } from '../query/v2/useCacheManager';

type UsePedagogicalContentsProps = {
  moduleId: number;
  courseLineId: number;
};

const usePedagogicalContents = ({
  moduleId,
  courseLineId,
}: UsePedagogicalContentsProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.pedContentTab',
  });
  const { tree } = useTreeCourses();

  const listCacheManager = useListCache(
    coursePathsQueryKeys.list({
      courseLineId: [courseLineId],
      pageSize: ORDENABLE_PAGE_SIZE,
    }).queryKey,
  );

  const {
    results: coursePaths,
    isInitialLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    cacheManager,
  } = useInfiniteService({
    ...coursePathsQueryKeys.list({ moduleId: [moduleId], uniqueCourse: true })
      ._ctx.infinity,
    staleTime: REQUEST_STALE_TIME_IN_MS,
    keepPreviousData: true,
  });

  const createMutation = useMutation({
    mutationFn: (data: CourseFormFields) =>
      createCoursePath({
        course: { ...data, moduleId },
      }),
    onSuccess: coursePath => {
      cacheManager.add(coursePath);
      listCacheManager.add(coursePath);
      const parentKey = mountKey({ type: 'module', id: moduleId });
      tree?.current.insert(
        {
          id: mountKey({ type: 'course', id: coursePath.slug }),
          name: coursePath.course.name,
          type: 'course',
          realId: coursePath.slug,
        },
        parentKey,
      );
      alert.success(t('createSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (slug: string) => deleteCoursePath(slug),
    onSuccess: (_, slug) => {
      const target = coursePaths.find(cp => cp.slug === slug);
      if (target) {
        cacheManager.remove(target.id);
        listCacheManager.remove(target.id);
      }
      tree?.current.delete(mountKey({ type: 'course', id: slug }));
      alert.success(t('deleteSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  return {
    coursePaths,
    isLoading: isInitialLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    cacheManager,
    createCoursePath: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    deleteCoursePath: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
  };
};

export default usePedagogicalContents;
